// HAWELO — Shopify Storefront API client (headless, browser-safe)
//
// Uses the public Storefront access token to query Shopify GraphQL directly
// from the browser. Zero server required.
//
// Docs: https://shopify.dev/docs/api/storefront

const DOMAIN = process.env.REACT_APP_SHOPIFY_DOMAIN;
const TOKEN = process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN;
const VERSION = process.env.REACT_APP_SHOPIFY_API_VERSION || '2024-10';

const ENDPOINT = `https://${DOMAIN}/api/${VERSION}/graphql.json`;

export async function storefront(query, variables = {}) {
  const r = await fetch(ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Shopify-Storefront-Access-Token': TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });
  const json = await r.json();
  if (json.errors) {
    console.error('[Storefront]', json.errors);
    throw new Error(json.errors[0]?.message || 'Storefront API error');
  }
  return json.data;
}

// ──────────────────────────────────────────────────
// Queries
// ──────────────────────────────────────────────────

const PRODUCT_FIELDS = `
  id
  handle
  title
  description
  productType
  tags
  availableForSale
  featuredImage { url altText }
  images(first: 6) { edges { node { url altText } } }
  priceRange {
    minVariantPrice { amount currencyCode }
  }
  variants(first: 20) {
    edges {
      node {
        id
        title
        availableForSale
        price { amount currencyCode }
        compareAtPrice { amount currencyCode }
      }
    }
  }
`;

export const Q_PRODUCTS = `
  query Products($first: Int!) {
    products(first: $first) {
      edges { node { ${PRODUCT_FIELDS} } }
    }
  }
`;

export const Q_PRODUCT_BY_HANDLE = `
  query ProductByHandle($handle: String!) {
    product(handle: $handle) { ${PRODUCT_FIELDS} }
  }
`;

export const Q_COLLECTIONS = `
  query Collections($first: Int!) {
    collections(first: $first) {
      edges {
        node {
          id
          handle
          title
          description
        }
      }
    }
  }
`;

export const Q_COLLECTION_BY_HANDLE = `
  query CollectionByHandle($handle: String!, $first: Int!) {
    collection(handle: $handle) {
      id
      handle
      title
      description
      products(first: $first) { edges { node { ${PRODUCT_FIELDS} } } }
    }
  }
`;

// ──────────────────────────────────────────────────
// Cart (GraphQL Cart API — not the deprecated Checkout)
// ──────────────────────────────────────────────────

const CART_FIELDS = `
  id
  checkoutUrl
  totalQuantity
  cost {
    subtotalAmount { amount currencyCode }
    totalAmount { amount currencyCode }
  }
  lines(first: 50) {
    edges {
      node {
        id
        quantity
        merchandise {
          ... on ProductVariant {
            id
            title
            price { amount currencyCode }
            image { url altText }
            product { id handle title }
          }
        }
      }
    }
  }
`;

export const M_CART_CREATE = `
  mutation CartCreate($lines: [CartLineInput!]) {
    cartCreate(input: { lines: $lines }) {
      cart { ${CART_FIELDS} }
      userErrors { field message }
    }
  }
`;

export const Q_CART = `
  query Cart($id: ID!) {
    cart(id: $id) { ${CART_FIELDS} }
  }
`;

export const M_CART_LINES_ADD = `
  mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart { ${CART_FIELDS} }
      userErrors { field message }
    }
  }
`;

export const M_CART_LINES_UPDATE = `
  mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart { ${CART_FIELDS} }
      userErrors { field message }
    }
  }
`;

export const M_CART_LINES_REMOVE = `
  mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart { ${CART_FIELDS} }
      userErrors { field message }
    }
  }
`;

// ──────────────────────────────────────────────────
// Helpers: normalize edges to plain arrays
// ──────────────────────────────────────────────────
export function flattenEdges(connection) {
  if (!connection) return [];
  return connection.edges.map((e) => e.node);
}

export function normaliseProduct(p) {
  if (!p) return null;
  return {
    ...p,
    images: flattenEdges(p.images),
    variants: flattenEdges(p.variants),
    price: Number(p.priceRange?.minVariantPrice?.amount || 0),
    currency: p.priceRange?.minVariantPrice?.currencyCode || 'INR',
  };
}

export function normaliseCart(cart) {
  if (!cart) return null;
  return {
    id: cart.id,
    checkoutUrl: cart.checkoutUrl,
    totalQuantity: cart.totalQuantity,
    subtotal: Number(cart.cost?.subtotalAmount?.amount || 0),
    currency: cart.cost?.subtotalAmount?.currencyCode || 'INR',
    lines: flattenEdges(cart.lines).map((line) => ({
      id: line.id,
      quantity: line.quantity,
      variantId: line.merchandise?.id,
      variantTitle: line.merchandise?.title,
      productTitle: line.merchandise?.product?.title,
      productHandle: line.merchandise?.product?.handle,
      productType: line.merchandise?.product?.productType,
      image: line.merchandise?.image?.url,
      price: Number(line.merchandise?.price?.amount || 0),
    })),
  };
}

export function formatMoney(amount, currency = 'INR') {
  const num = typeof amount === 'number' ? amount : Number(amount || 0);
  if (currency === 'INR') return `₹${num.toFixed(0)}`;
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(num);
}
